'use client';

import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { Paragraph } from '../../ui/paragraph';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';
import { MdOutlineArrowCircleRight } from 'react-icons/md';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ProductShortDescription() {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const product = useVariantsStore(state => state.product);

  const paraRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [expandedHeight, setExpandedHeight] = useState(0);

  const content = selectedVariant?.short_description ?? product?.short_description;

  useEffect(() => {
    setIsExpanded(false);
  }, [content]);

  useEffect(() => {
    if (isExpanded) return;
    const el = paraRef.current;
    if (!el) return;

    const computed = getComputedStyle(el);
    const lineHeightPx = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.5;
    const collapsed = Math.ceil(lineHeightPx * 2);
    const expanded = el.scrollHeight;

    setCollapsedHeight(collapsed);
    setExpandedHeight(expanded);
    setIsOverflowing(expanded > collapsed + 2);
  }, [content, isExpanded]);

  if (!content) return null;

  return (
    <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
      <div
        className="w-full overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{
          maxHeight: collapsedHeight ? (isExpanded ? expandedHeight : collapsedHeight) : undefined,
        }}
      >
        <Paragraph
          ref={paraRef}
          className="w-full"
          size="base"
          color="muted-foreground"
          innerHtml={content}
        />
      </div>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          className={cn('generic-hover', 'flex items-center justify-start gap-2 text-walnut')}
        >
          <MdOutlineArrowCircleRight
            size={18}
            className={cn('transition-transform duration-300', isExpanded && 'rotate-90')}
          />
          {isExpanded ? t('text528') : t('text452')}
        </button>
      )}
    </div>
  );
}
